import { useEffect, useState } from 'react';
import { listPatients } from '../../lib/adapters';
import { adminGetAlertPolicy } from '../../lib/admin';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { getMovementLive, residentSensorIds } from '../../lib/movement';

export const DEFAULT_POLICY = { night_hours_start: '22:00', night_hours_end: '06:00', timezone: 'UTC', device_silence_threshold_min: 30 };
export const MAX_OFFSET = 28; // motion history is retained 30 days

// Policy + motion sensors + residents for the current org.
export function useNightData(orgId) {
  const [base, setBase] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_super_admin === true;
  useEffect(() => {
    if (!orgId) return undefined;
    let alive = true;
    setBase(null);
    Promise.all([
      (isAdmin ? adminGetAlertPolicy(orgId).then((p) => ({ ...DEFAULT_POLICY, ...p, fallback: false })) : Promise.reject())
        .catch(() => api.get(`/organizations/${orgId}/night-summary`).then((r) => {
          const w = r.data?.window || {};
          return { ...DEFAULT_POLICY, timezone: w.timezone || DEFAULT_POLICY.timezone, night_hours_start: w.night_hours_start || DEFAULT_POLICY.night_hours_start, night_hours_end: w.night_hours_end || DEFAULT_POLICY.night_hours_end, fallback: false };
        }))
        .catch(() => ({ ...DEFAULT_POLICY, fallback: true })),
      getMovementLive().then((d) => d.devices || []).catch(() => []),
      listPatients({ limit: 200 }).then((r) => r.items).catch(() => []),
    ]).then(([policy, sensors, patients]) => alive && setBase({ policy, sensors, patients }));
    return () => { alive = false; };
  }, [orgId, isAdmin]);
  return base;
}

// Report subjects: residents with their own sensors, plus ONE row per shared room.
export function buildSubjects({ patients, sensors }, { family = false } = {}) {
  const perRoom = patients.reduce((m, p) => (p.room_id ? { ...m, [p.room_id]: (m[p.room_id] || 0) + 1 } : m), {});
  const subjects = [];
  const rooms = new Map();
  const noSensor = [];
  patients.forEach((p) => {
    const { ids, shared, roomIds } = residentSensorIds(p, sensors, perRoom[p.room_id] || 0);
    if (ids.length) subjects.push({ kind: 'patient', id: p.id, name: p.name, to: `/patients/${p.id}`, ids });
    if (shared) {
      const r = rooms.get(p.room_id) || {
        kind: 'room', id: p.room_id, to: `/rooms/${p.room_id}`, ids: roomIds, members: [],
        name: sensors.find((s) => s.assigned_room_id === p.room_id)?.room_name || p.room_label || '—',
      };
      r.members.push(p.name);
      rooms.set(p.room_id, r);
    }
    if (!ids.length && !shared) noSensor.push(p);
  });
  return { subjects: family ? subjects : [...subjects, ...rooms.values()], noSensor };
}
